import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ArrowRightSvg } from 'assets/arrowRight.svg';
import CancelButton from 'atoms/CancelButton';
import Button from 'atoms/Button';
import ModalBackdrop from 'molecules/Modal';
import { filterProps } from 'utils/helpers';

import { ONBOARDING_NODE_ID, STACK_LEVEL } from '../../constants';

import { usePromptModalService } from './hooks';

import type { InterpolationWithTheme } from 'utils/types';
import { FlatJourneyProps, JourneyProps } from 'features/Onboarding/journeys/types';

const Backdrop = styled(ModalBackdrop, filterProps('hasLightBackdrop'))<{
  lightBackdrop: boolean;
}>`
  ${({ theme: { colors, depths, utils }, lightBackdrop }) => css`
    z-index: ${depths.priority + STACK_LEVEL.background};

    &&& {
      background-color: ${utils.alphaHex(colors.neutral.black, lightBackdrop ? 30 : 60)};
    }
  `}
`;

enum MODAL_AREAS {
  title = 'title',
  description = 'description',
  primary = 'primary',
  or = 'or',
  secondary = 'secondary',
}

const modalStyle: InterpolationWithTheme = ({ colors, mq, utils }) => css`
  grid-template-areas: ${utils.composeTemplateAreas([
    [MODAL_AREAS.title],
    [MODAL_AREAS.description],
    [MODAL_AREAS.primary],
    [MODAL_AREAS.or],
    [MODAL_AREAS.secondary],
  ])};
  background-color: ${colors.neutral.white};

  ${mq.md.up} {
    min-width: 68rem;
    padding: 6rem;
    justify-self: center;
    align-self: center;
    border-radius: 4rem;
  }

  ${mq.md.down} {
    grid-template-areas: ${utils.composeTemplateAreas([
      ['.'],
      [MODAL_AREAS.title],
      [MODAL_AREAS.description],
      ['.'],
      [MODAL_AREAS.primary],
      [MODAL_AREAS.or],
      [MODAL_AREAS.secondary],
    ])};
    grid-template-rows: 1fr min-content min-content 1fr min-content min-content min-content;
    padding-bottom: 7rem;
  }
`;

const StyledCancelButton = styled(CancelButton)`
  ${({ theme: { colors, mq, utils } }) => css`
    & > svg {
      fill: ${utils.alphaHex(colors.primary.dark, 55)};
    }

    ${mq.md.up} {
      grid-area: ${MODAL_AREAS.title};
      justify-self: flex-end;
      align-self: flex-start;
      transform: translate(100%, -100%);
    }

    ${mq.md.down} {
      display: none;
    }
  `}
`;

const Title = styled.h1`
  ${({ theme: { mq, tp } }) => css`
    ${tp.h1};

    grid-area: ${MODAL_AREAS.title};
    margin: 0;
    padding: 0 4rem;
    text-align: center;

    ${mq.md.down} {
      padding: 0 3rem;
    }
  `}
`;

const Description = styled.p`
  ${({ theme: { mq, tp } }) => css`
    ${tp.p1};

    grid-area: ${MODAL_AREAS.description};
    margin: 1rem 0 4.5rem;
    padding: 0 4rem;
    text-align: center;

    ${mq.md.down} {
      padding: 0 3rem;
      margin-bottom: 0;
    }
  `}
`;

const PrimaryButton = styled(Button)`
  grid-area: ${MODAL_AREAS.primary};
`;

const PromptModal: React.FC<FlatJourneyProps> = props => {
  const { t } = useTranslation();
  const {
    showBackdrop,
    showModal,
    lessonsJourneyIsActive,
    handleClose,
    handleBackdropClick,
    handleStart,
  } = usePromptModalService(props as JourneyProps);

  return (
    <Backdrop
      id={ONBOARDING_NODE_ID}
      isOpen={showModal}
      onClick={handleBackdropClick}
      showBackdropOverride={showBackdrop}
      lightBackdrop={!showModal}
      classNames={{
        modal: modalStyle,
      }}
      scrollLock={!(!showModal && lessonsJourneyIsActive)}
    >
      <StyledCancelButton onClick={handleClose} />
      <Title>{t('onboardingTooltips.welcome.title')}</Title>
      <Description>
        {t('onboardingTooltips.welcome.description')}
        <br />
        {t('onboardingTooltips.welcome.descriptionLine2')}
      </Description>
      <PrimaryButton onClick={handleStart}>
        <>
          {t('onboardingTooltips.welcome.exploreApp')}
          <ArrowRightSvg />
        </>
      </PrimaryButton>
    </Backdrop>
  );
};

export default PromptModal;
